/**
 * StockAdjustDrawer — ajustar estoque de um produto físico + ver histórico.
 *
 * Props:
 *   isOpen, onClose
 *   physicalProduct   — item (obrigatório)
 *   currentStock      — { current_quantity, reserved_quantity, alert_threshold }
 *   onSaved(newStock)
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { stockV2API } from '../../../services/api/v2/stock';
import Drawer from './Drawer';

const StockAdjustDrawer = ({ isOpen, onClose, physicalProduct, currentStock, onSaved }) => {
  const [mode, setMode] = useState('add');     // 'add' | 'adjust'
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');
  const [movements, setMovements] = useState([]);
  const [loadingMov, setLoadingMov] = useState(false);

  // Reset ao abrir
  useEffect(() => {
    if (!isOpen) return;
    setMode('add');
    setQuantity('');
    setReason('');
    setTopError('');
    // Carrega últimas movimentações
    (async () => {
      if (!physicalProduct?.id) return;
      try {
        setLoadingMov(true);
        const res = await stockV2API.movements(physicalProduct.id, { limit: 8 });
        const list = res.data?.movements || res.data?.data || [];
        setMovements(list);
      } catch (_) {
        setMovements([]);
      } finally {
        setLoadingMov(false);
      }
    })();
  }, [isOpen, physicalProduct]);

  const handleSubmit = async () => {
    if (!physicalProduct?.id || submitting) return;
    const qty = parseInt(quantity, 10);
    if (!qty || qty === 0) {
      setTopError('Informe uma quantidade diferente de zero');
      return;
    }
    if (mode === 'adjust' && !reason.trim()) {
      setTopError('Ajuste manual exige um motivo');
      return;
    }
    try {
      setSubmitting(true);
      setTopError('');
      // Backend: /add espera { quantity }, /adjust espera { delta } (semântica
      // diferente: add é incremento, adjust é delta livre que pode ser negativo).
      const payload = mode === 'add'
        ? { quantity: qty, reason: reason.trim() || null }
        : { delta: qty, reason: reason.trim() || null };
      const res = mode === 'add'
        ? await stockV2API.add(physicalProduct.id, payload)
        : await stockV2API.adjust(physicalProduct.id, payload);
      const saved = res.data?.stock || res.data?.data || res.data;
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const data = err.response?.data || {};
      setTopError(data.message || err.message || 'Erro ao registrar movimento');
    } finally {
      setSubmitting(false);
    }
  };

  const current = Number(currentStock?.current_quantity ?? 0);
  const reserved = Number(currentStock?.reserved_quantity ?? 0);
  const available = current - reserved;
  const threshold = Number(currentStock?.alert_threshold ?? 50);
  const lowStock = current <= threshold;

  const previewQty = parseInt(quantity, 10) || 0;
  const newBalance = mode === 'add'
    ? current + previewQty
    : current + previewQty; // adjust aceita negativo

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Movimentar estoque"
      subtitle={physicalProduct?.name}
      icon="mdi:warehouse"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={mode === 'add' ? 'Adicionar ao estoque' : 'Registrar ajuste'}
    >
      {/* Resumo atual */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
      }}>
        <div style={{ padding: 14, background: '#fafafa', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>Atual</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: lowStock ? '#b45309' : '#047857' }}>{current}</div>
        </div>
        <div style={{ padding: 14, background: '#fafafa', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>Reservado</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{reserved}</div>
        </div>
        <div style={{ padding: 14, background: '#fafafa', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>Disponível</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{available}</div>
        </div>
      </div>

      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      {/* Modo */}
      <div className="drawer-field">
        <span className="drawer-label">Tipo de movimentação</span>
        <div className="drawer-toggle-group">
          <button
            type="button"
            className={`drawer-toggle-btn ${mode === 'add' ? 'is-active' : ''}`}
            onClick={() => setMode('add')}
          >
            <Icon icon="mdi:plus-circle-outline" width="20" />
            <span className="drawer-toggle-btn--label">Entrada</span>
            <span className="drawer-toggle-btn--desc">Adicionar unidades</span>
          </button>
          <button
            type="button"
            className={`drawer-toggle-btn ${mode === 'adjust' ? 'is-active' : ''}`}
            onClick={() => setMode('adjust')}
          >
            <Icon icon="mdi:swap-horizontal" width="20" />
            <span className="drawer-toggle-btn--label">Ajuste manual</span>
            <span className="drawer-toggle-btn--desc">Pode ser negativo</span>
          </button>
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Quantidade {mode === 'adjust' && '(use negativo p/ retirar)'}</span>
        <input
          className="drawer-input"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder={mode === 'add' ? 'Ex: 50' : 'Ex: -10'}
        />
        {previewQty !== 0 && (
          <span className="drawer-help">
            Novo saldo: <b>{newBalance}</b> {newBalance < threshold && '⚠ abaixo do limite de alerta'}
          </span>
        )}
      </div>

      <div className="drawer-field">
        <span className={`drawer-label ${mode === 'adjust' ? 'drawer-label--required' : ''}`}>
          Motivo {mode === 'add' ? '(opcional)' : ''}
        </span>
        <textarea
          className="drawer-textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={mode === 'add'
            ? 'Ex: Reposição mensal, lote XYZ recebido...'
            : 'Ex: Contagem física, perda, devolução não-cancelada...'}
          rows={3}
        />
      </div>

      {/* Histórico */}
      <div className="drawer-field">
        <span className="drawer-label">Últimas movimentações</span>
        {loadingMov ? (
          <span className="drawer-help">Carregando histórico...</span>
        ) : movements.length === 0 ? (
          <span className="drawer-help">Sem movimentações ainda.</span>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {movements.map((m) => {
              const positive = Number(m.quantity || 0) > 0;
              return (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    background: '#fafafa',
                    borderRadius: 8,
                    fontSize: 12,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Icon
                    icon={positive ? 'mdi:arrow-up-bold-circle-outline' : 'mdi:arrow-down-bold-circle-outline'}
                    width="16"
                    style={{ color: positive ? '#047857' : '#b91c1c', flexShrink: 0 }}
                  />
                  <span style={{ flex: 1, color: '#374151' }}>
                    {(() => {
                      // Backend retorna 'manual_adjustment', 'initial_stock', etc.
                      // Convertendo pra texto amigável.
                      const labels = {
                        manual_adjustment: 'Ajuste manual',
                        initial_stock: 'Estoque inicial',
                        purchase: 'Compra/Entrada',
                        sale: 'Venda',
                        return: 'Devolução',
                        loss: 'Perda',
                      };
                      const raw = m.movement_type;
                      if (raw && labels[raw]) return labels[raw];
                      if (raw) return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                      return positive ? 'Entrada' : 'Saída';
                    })()}
                    {m.reason && <span style={{ color: '#6b7280' }}> — {m.reason}</span>}
                  </span>
                  <span style={{ fontWeight: 700, color: positive ? '#047857' : '#b91c1c', fontVariantNumeric: 'tabular-nums' }}>
                    {positive ? '+' : ''}{m.quantity}
                  </span>
                  <span style={{ color: '#9ca3af', fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>
                    {m.created_at ? new Date(m.created_at).toLocaleDateString('pt-BR') : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default StockAdjustDrawer;
